import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface EventApproval {
  id: string;
  event_id: string;
  client_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'needs_changes';
  approval_notes?: string;
  approved_by?: string;
  approved_at?: string;
  requested_at: string;
  created_at: string;
  updated_at: string;
  event_name?: string;
}

interface ClientFeedback {
  id: string;
  event_id: string;
  client_id: string;
  feedback_type: 'general' | 'revision_request' | 'approval' | 'question';
  message: string;
  status: 'open' | 'addressed' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  response_message?: string;
  responded_by?: string;
  responded_at?: string;
  created_at: string;
  updated_at: string;
  event_name?: string;
}

export const useClientPortal = (clientId: string) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [approvals, setApprovals] = useState<EventApproval[]>([]);
  const [feedback, setFeedback] = useState<ClientFeedback[]>([]);
  const [loading, setLoading] = useState(true);

  const loadClientData = async () => {
    if (!clientId) return;

    try {
      setLoading(true);

      // Use SECURITY DEFINER functions to fetch data (bypasses RLS for unauthenticated clients)
      const [eventsResult, approvalsResult, feedbackResult] = await Promise.all([
        supabase.rpc('get_client_events', { p_client_id: clientId }),
        supabase.rpc('get_client_approvals', { p_client_id: clientId }),
        supabase.rpc('get_client_feedback', { p_client_id: clientId })
      ]);

      if (eventsResult.error) {
        console.error('Error loading events:', eventsResult.error);
      }
      if (approvalsResult.error) {
        console.error('Error loading approvals:', approvalsResult.error);
      }
      if (feedbackResult.error) {
        console.error('Error loading feedback:', feedbackResult.error);
      }

      setEvents(eventsResult.data || []);
      setApprovals((approvalsResult.data as EventApproval[]) || []);
      setFeedback((feedbackResult.data as ClientFeedback[]) || []);
    } catch (error) {
      console.error('Error loading client portal data:', error);
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const createApprovalRequest = async (eventId: string, notes?: string, submittedBy?: string) => {
    try {
      const { data, error } = await supabase
        .from('event_approvals')
        .insert({
          event_id: eventId,
          client_id: clientId,
          approval_notes: notes,
          status: 'pending',
          submitted_by: submittedBy
        })
        .select()
        .single();

      if (error) throw error;

      setApprovals(prev => [data as EventApproval, ...prev]);
      toast.success('Approval request created');
      return data;
    } catch (error) {
      console.error('Error creating approval request:', error);
      toast.error('Failed to create approval request');
      throw error;
    }
  };

  const updateApprovalStatus = async (
    approvalId: string, 
    status: 'approved' | 'rejected' | 'needs_changes',
    notes?: string
  ) => {
    try {
      // Use SECURITY DEFINER function for client portal access
      const { data, error } = await supabase.rpc('update_client_approval_status', {
        p_approval_id: approvalId,
        p_client_id: clientId,
        p_status: status,
        p_notes: notes
      });

      if (error) throw error;

      // Reload approvals to get updated data
      const { data: approvalsData } = await supabase.rpc('get_client_approvals', { 
        p_client_id: clientId 
      });
      
      setApprovals((approvalsData as EventApproval[]) || []);

      return data;
    } catch (error) {
      console.error('Error updating approval status:', error);
      throw error;
    }
  };

  const submitFeedback = async (
    eventId: string,
    feedbackType: 'general' | 'revision_request' | 'approval' | 'question',
    message: string,
    priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium',
    submittedBy?: string
  ) => {
    try {
      // Use SECURITY DEFINER function for client portal access
      const { data: feedbackId, error } = await supabase.rpc('submit_client_feedback', {
        p_client_id: clientId,
        p_event_id: eventId,
        p_feedback_type: feedbackType,
        p_message: message,
        p_priority: priority
      });

      if (error) throw error;

      // Reload feedback to get the new item with all fields
      const { data: feedbackData } = await supabase.rpc('get_client_feedback', { 
        p_client_id: clientId 
      });
      
      setFeedback((feedbackData as ClientFeedback[]) || []);

      return { id: feedbackId };
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  };

  const respondToFeedback = async (feedbackId: string, responseMessage: string) => {
    try {
      const { data, error } = await supabase
        .from('client_feedback')
        .update({
          response_message: responseMessage,
          responded_by: user?.id,
          responded_at: new Date().toISOString(),
          status: 'addressed'
        })
        .eq('id', feedbackId)
        .select()
        .single();

      if (error) throw error;

      setFeedback(prev => 
        prev.map(item => 
          item.id === feedbackId ? data as ClientFeedback : item
        )
      );

      return data;
    } catch (error) {
      console.error('Error responding to feedback:', error);
      throw error;
    }
  };

  const generateClientPortalLink = async (clientId: string, expiresInHours: number = 24) => {
    try {
      const sessionToken = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + expiresInHours);

      const { data, error } = await supabase
        .from('client_portal_sessions')
        .insert({
          client_id: clientId,
          session_token: sessionToken,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      const portalUrl = `${window.location.origin}/client-portal/${sessionToken}`;
      return { portalUrl, sessionToken, expiresAt };
    } catch (error) {
      console.error('Error generating client portal link:', error);
      throw error;
    }
  };

  return {
    events,
    approvals,
    feedback,
    loading,
    createApprovalRequest,
    updateApprovalStatus,
    submitFeedback,
    respondToFeedback,
    generateClientPortalLink,
    refetch: loadClientData
  };
};
